/**
 * Quick Actions Utilities
 *
 * Provides action handlers for StartPage quick actions.
 * Opens new tabs for various workspace types with the correct connection context.
 */

import { useConnectionStore } from '../../../connections/store/connectionStore';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useTerminalStore } from '../../../terminal';
import { toast } from '../../../../components/ui/toaster';
import { openDashboardTab } from '../../dashboards/utils/openDashboardTab';
import { safeConnectionName, safeConnectionServer } from '../../../../types/safeConnectionDisplay';

/**
 * Get connection info for a given connection ID.
 * Returns connection details needed to populate tab connectionInfo.
 */
function getConnectionInfo(connectionId: string, database?: string) {
  const { connections, activeConnections } = useConnectionStore.getState();
  const savedConnection = connections.find((c) => c.id === connectionId);
  const activeConnection = activeConnections.find((c) => c.id === connectionId);

  if (!savedConnection && !activeConnection) {
    return null;
  }

  const connectionConfig = savedConnection || activeConnection!.connection;

  return {
    connectionId,
    connectionName: safeConnectionName(connectionConfig, 'Unnamed Connection'),
    serverHost: safeConnectionServer(connectionConfig),
    database: database || activeConnection?.connection.database,
  };
}

/**
 * Open a new query tab with connection context.
 */
export function openNewQueryTab(connectionId: string, database?: string): void {
  const connectionInfo = getConnectionInfo(connectionId, database);
  if (!connectionInfo) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();

  addTab({
    title: `Query-${queryNumber}`,
    type: 'query',
    connectionInfo,
  });
}

/**
 * Open the Table Explorer dialog.
 * User selects a table in the dialog, then a tab is created with the selected table.
 * For a specific table, use openTableExplorerTab from table-explorer utils.
 */
export function openTableExplorerFromStartPage(
  connectionId: string,
  database: string
): void {
  const connectionInfo = getConnectionInfo(connectionId, database);
  if (!connectionInfo) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { activeConnections } = useConnectionStore.getState();
  const activeConnection = activeConnections.find((c) => c.id === connectionId);
  const engine = activeConnection?.connection.engine ?? 'mssql';

  // Import and use the dialog store to open the Table Explorer dialog
  // This ensures consistent UX - user always selects a table first via dialog
  import('../../table-explorer/store/tableExplorerDialogStore').then(
    ({ useTableExplorerDialogStore }) => {
      useTableExplorerDialogStore.getState().openDialog({
        connectionId,
        connectionName: connectionInfo.connectionName,
        serverHost: connectionInfo.serverHost,
        database,
        engineId: engine as 'mssql' | 'pgsql' | 'sqlite',
      });
    }
  );
}

/**
 * Open a table designer tab in create mode.
 */
export function openTableDesignerFromStartPage(
  connectionId: string,
  database: string
): void {
  const connectionInfo = getConnectionInfo(connectionId, database);
  if (!connectionInfo) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { activeConnections } = useConnectionStore.getState();
  const activeConnection = activeConnections.find((c) => c.id === connectionId);
  const engine = activeConnection?.connection.engine ?? 'mssql';
  const defaultSchema = engine === 'pgsql' ? 'public' : engine === 'sqlite' ? '' : 'dbo';

  const { addTab } = useWorkspaceStore.getState();

  addTab({
    title: 'New Table',
    type: 'table-designer',
    connectionInfo,
    content: {
      mode: 'create',
      tableName: '',
      schema: defaultSchema,
      database,
      columns: [],
      isDirty: false,
      isLoading: false,
      isApplying: false,
    },
  });
}

/**
 * Open a schema overview tab for a database.
 */
export function openSchemaOverviewFromStartPage(
  connectionId: string,
  database: string
): void {
  const connectionInfo = getConnectionInfo(connectionId, database);
  if (!connectionInfo) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const { addTab } = useWorkspaceStore.getState();

  addTab({
    title: `Overview: ${database}`,
    type: 'schema-overview',
    connectionInfo,
    content: {
      connectionId,
      databaseName: database,
      filterMode: 'all',
      isLoading: true,
    },
  });
}

/**
 * Toggle terminal panel (desktop only).
 */
export function toggleTerminal(): void {
  useTerminalStore.getState().togglePanel();
}

/**
 * Open (or focus) the Dashboards workspace tab from a Home page connection
 * card (S21). Deliberately does NOT prefill the picked connection — the
 * starter zero-state (`StarterDashboards.tsx`) already defaults to the
 * first connected connection and lets the user switch, so threading a hint
 * through here would duplicate that logic for one fewer click.
 */
export function openDashboardsFromStartPage(): void {
  openDashboardTab();
}
